import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Search, Send, Loader2, Inbox, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { CATEGORY_META } from "../lib/constants";

/** "📧 Outreach Email" tab for the admin dashboard. */
export default function OutreachTab() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25, total_pages: 0 });
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [emailDraft, setEmailDraft] = useState({});
  const [filters, setFilters] = useState({
    q: "", category: "", city: "", with_email_only: false, page: 1,
  });
  const [cities, setCities] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/outreach/stats");
      setStats(data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page, limit: 25,
        q: filters.q || undefined,
        category: filters.category || undefined,
        city: filters.city || undefined,
        with_email_only: filters.with_email_only || undefined,
      };
      const { data } = await api.get("/admin/outreach/businesses", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/cities"); setCities(data); } catch (_) {}
    })();
  }, []);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 1 }));

  const sendOne = async (biz, overrideEmail) => {
    setBusyIds((s) => new Set(s).add(biz.id));
    try {
      const { data } = await api.post("/admin/outreach/send", {
        business_id: biz.id,
        email: overrideEmail || undefined,
      });
      if (data.ok) {
        toast.success(`✅ Email envoyé à ${biz.name}`);
        setData((d) => ({
          ...d,
          items: d.items.map((b) => b.id === biz.id ? {
            ...b,
            email: overrideEmail || b.email,
            outreach_sent_at: new Date().toISOString(),
            outreach_email_used: data.email,
          } : b),
        }));
        fetchStats();
      } else {
        toast.error(`Échec: ${data.error || data.warning || "envoi"}`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur d'envoi";
      toast.error(msg);
    } finally {
      setBusyIds((s) => { const n = new Set(s); n.delete(biz.id); return n; });
    }
  };

  const sendBatch = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/outreach/send-batch", { business_ids: ids });
      const msg = `Envoyés: ${data.sent.length} · Sans email: ${data.skipped.length} · Échecs: ${data.failed.length}`;
      if (data.truncated_by_daily_cap) toast.warning(`Limite journalière atteinte. ${msg}`);
      else toast.success(msg);
      setSelected(new Set());
      fetchList(); fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur batch");
    } finally { setLoading(false); }
  };

  const allVisibleSelected = data.items.length > 0 && data.items.every((b) => selected.has(b.id));
  const toggleAll = () => {
    setSelected((s) => {
      if (allVisibleSelected) {
        const n = new Set(s); data.items.forEach((b) => n.delete(b.id)); return n;
      }
      const n = new Set(s); data.items.forEach((b) => n.add(b.id)); return n;
    });
  };
  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  return (
    <div data-testid="outreach-tab">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Non-réclamées" value={stats?.total_unclaimed} accent="green" />
        <StatCard label="Avec email" value={stats?.with_email} accent="gold" />
        <StatCard label="Emails envoyés" value={stats?.total_sent} accent="dark" />
        <StatCard
          label={`Envoyés aujourd'hui · ${stats?.daily_cap || 50}/jour`}
          value={`${stats?.today_sent || 0} / ${stats?.daily_cap || 50}`}
          accent="red"
          highlight={stats?.today_sent >= (stats?.daily_cap || 50)}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-white p-3 rounded-xl border border-stone-200">
        <div className="flex items-center gap-2 px-3 h-10 rounded-lg bg-stone-50 border border-stone-200 flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-stone-400" />
          <input
            value={filters.q}
            onChange={(e) => setFilter("q", e.target.value)}
            placeholder="Rechercher par nom..."
            className="bg-transparent flex-1 focus:outline-none text-sm"
            data-testid="outreach-search-input"
          />
        </div>
        <select value={filters.category} onChange={(e) => setFilter("category", e.target.value)}
                className="h-10 rounded-lg border border-stone-200 px-3 text-sm bg-white" data-testid="outreach-cat-filter">
          <option value="">Toutes catégories</option>
          {Object.entries(CATEGORY_META).map(([s, m]) => <option key={s} value={s}>{m.name}</option>)}
        </select>
        <select value={filters.city} onChange={(e) => setFilter("city", e.target.value)}
                className="h-10 rounded-lg border border-stone-200 px-3 text-sm bg-white" data-testid="outreach-city-filter">
          <option value="">Toutes villes</option>
          {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={filters.with_email_only}
                 onChange={(e) => setFilter("with_email_only", e.target.checked)}
                 data-testid="outreach-email-only" />
          Avec email seulement
        </label>
        {selected.size > 0 && (
          <button onClick={sendBatch}
                  className="ml-auto h-10 px-4 rounded-lg bg-lokaso-green text-white text-sm font-semibold inline-flex items-center gap-2"
                  data-testid="outreach-send-batch">
            <Send className="w-4 h-4" /> Envoyer la sélection ({selected.size})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-lokaso-cream">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="outreach-select-all" />
                </th>
                <th className="text-left px-3 py-3">#</th>
                <th className="text-left px-3 py-3">Nom</th>
                <th className="text-left px-3 py-3 hidden md:table-cell">Catégorie</th>
                <th className="text-left px-3 py-3 hidden md:table-cell">Ville</th>
                <th className="text-left px-3 py-3">Email</th>
                <th className="text-left px-3 py-3">Statut</th>
                <th className="text-right px-3 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-8 text-center text-stone-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block" /> Chargement…
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-stone-500">
                  <Inbox className="w-5 h-5 inline-block mr-2" /> Aucune entreprise correspondante.
                </td></tr>
              )}
              {!loading && data.items.map((b, i) => {
                const sentAt = b.outreach_sent_at ? new Date(b.outreach_sent_at).toLocaleDateString("fr-FR") : null;
                const status = sentAt ? "sent" : b.email ? "uncontacted" : "no_email";
                const busy = busyIds.has(b.id);
                const draft = emailDraft[b.id] || "";
                return (
                  <tr key={b.id} className="border-t border-stone-100" data-testid={`outreach-row-${b.id}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggleOne(b.id)}
                             data-testid={`outreach-check-${b.id}`} />
                    </td>
                    <td className="px-3 py-3 text-stone-400">{(data.page - 1) * data.limit + i + 1}</td>
                    <td className="px-3 py-3 font-semibold">
                      <a href={`/business/${b.slug}`} target="_blank" rel="noreferrer" className="hover:text-lokaso-green">
                        {b.name}
                      </a>
                    </td>
                    <td className="px-3 py-3 capitalize hidden md:table-cell">{b.category_slug}</td>
                    <td className="px-3 py-3 capitalize hidden md:table-cell">{b.city_slug}</td>
                    <td className="px-3 py-3">
                      {b.email || (
                        <input
                          type="email"
                          value={draft}
                          onChange={(e) => setEmailDraft((s) => ({ ...s, [b.id]: e.target.value }))}
                          placeholder="Ajouter un email"
                          className="h-8 px-2 text-sm border border-stone-200 rounded-md w-40 focus:outline-none focus:border-lokaso-gold"
                          data-testid={`outreach-email-input-${b.id}`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {status === "sent" && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 font-semibold">
                          <CheckCircle2 className="w-3 h-3" /> Email envoyé — {sentAt}
                        </span>
                      )}
                      {status === "uncontacted" && (
                        <span className="px-2 py-1 rounded-full bg-stone-100 text-stone-700">Non contacté</span>
                      )}
                      {status === "no_email" && (
                        <span className="px-2 py-1 rounded-full bg-orange-50 text-orange-800">Pas d'email</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {b.email ? (
                        <button onClick={() => sendOne(b)} disabled={busy}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-lokaso-gold text-lokaso-dark text-xs font-semibold disabled:opacity-50"
                                data-testid={`outreach-send-${b.id}`}>
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Envoyer
                        </button>
                      ) : (
                        <button onClick={() => sendOne(b, draft)} disabled={busy || !draft}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-lokaso-green text-white text-xs font-semibold disabled:opacity-40"
                                data-testid={`outreach-add-send-${b.id}`}>
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Ajouter & Envoyer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 text-sm">
            <span className="text-stone-600">{data.total.toLocaleString("fr-FR")} entreprises</span>
            <div className="flex items-center gap-2">
              <button disabled={data.page <= 1}
                      onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                      className="p-1.5 rounded-lg border border-stone-200 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span>Page {data.page} / {data.total_pages}</span>
              <button disabled={data.page >= data.total_pages}
                      onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                      className="p-1.5 rounded-lg border border-stone-200 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = "green", highlight = false }) {
  const colors = {
    green: "border-lokaso-green/30 text-lokaso-green",
    gold: "border-lokaso-gold/40 text-lokaso-gold",
    dark: "border-stone-300 text-lokaso-dark",
    red: highlight ? "border-lokaso-red/40 text-lokaso-red bg-red-50" : "border-stone-300 text-stone-700",
  };
  return (
    <div className={`bg-white rounded-xl border ${colors[accent]} p-4`}>
      <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-bold mt-1">
        {value === undefined ? "—" : (typeof value === "number" ? value.toLocaleString("fr-FR") : value)}
      </div>
    </div>
  );
}
